import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { IconButton } from './IconButton';
import { Icon } from './Icon';
import { cx } from './cx';
import { typography } from './Text';
import { useT } from '../../modules/i18n';

export interface JsonViewProps {
  /** Arbitrary data — a log `meta`, an event payload, an error body. */
  data: unknown;
  /**
   * Wrap long lines instead of scrolling sideways. `overflow-wrap: anywhere`
   * breaks only where a break is actually needed, so indentation still reads —
   * unlike `break-all`, which breaks mid-token everywhere.
   */
  wrap?: boolean;
  /** Height cap for the scroll area; default `max-h-96`. */
  maxHeightClass?: string;
  className?: string;
}

interface Token {
  text: string;
  className?: string;
}

// Matches (in priority order): quoted strings (optionally followed by the
// colon that makes them an object key), true/false/null, and numbers.
const TOKEN_RE =
  /("(?:\\u[a-fA-F0-9]{4}|\\.|[^\\"])*"(\s*:)?)|(\btrue\b|\bfalse\b|\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

/**
 * Split a pretty-printed JSON string into tinted tokens — a tiny hand-rolled
 * highlighter (no external dependency): object keys tint brand, string
 * values green, booleans amber, `null` muted, numbers red-ish (reusing the
 * error token slot purely for visual contrast, not semantics), punctuation
 * stays the default body tone.
 */
function tokenize(json: string): Token[] {
  const tokens: Token[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((match = TOKEN_RE.exec(json)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ text: json.slice(lastIndex, match.index) });
    }
    const [full, stringToken, , boolOrNull, number] = match;
    if (stringToken !== undefined) {
      const isKey = /:\s*$/.test(stringToken);
      tokens.push({
        text: full,
        className: isKey ? 'text-brand-text' : 'text-success',
      });
    } else if (boolOrNull !== undefined) {
      tokens.push({
        text: full,
        className: boolOrNull === 'null' ? 'text-fg-muted' : 'text-warning',
      });
    } else if (number !== undefined) {
      tokens.push({ text: full, className: 'text-error' });
    } else {
      tokens.push({ text: full });
    }
    lastIndex = TOKEN_RE.lastIndex;
  }
  if (lastIndex < json.length) tokens.push({ text: json.slice(lastIndex) });
  return tokens;
}

/**
 * A copyable, lightly syntax-highlighted JSON viewer for arbitrary data (log
 * `meta`, event payloads, error bodies). Pretty-prints with 2-space indent; a
 * top-right copy button writes the raw JSON to the clipboard and flips to a
 * checkmark for a moment as confirmation. `wrap` trades sideways scrolling for
 * wrapped lines, which is what you want for payloads with long string values.
 */
export function JsonView({
  data,
  wrap = false,
  maxHeightClass = 'max-h-96',
  className,
}: JsonViewProps): JSX.Element {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(data ?? null, null, 2);
  const tokens = tokenize(json);

  const handleCopy = () => {
    navigator.clipboard
      ?.writeText(json)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard access denied/unavailable — nothing more we can do here.
      });
  };

  return (
    <div
      className={cx(
        'relative overflow-hidden rounded-lg border border-default bg-muted',
        className,
      )}
    >
      <IconButton
        aria-label={copied ? t('ui.jsonView.copied') : t('ui.jsonView.copy')}
        size="sm"
        variant="ghost"
        onPress={handleCopy}
        className="absolute right-2 top-2"
      >
        <Icon
          as={copied ? Check : Copy}
          size="sm"
          className={copied ? 'text-success' : undefined}
        />
      </IconButton>
      <pre
        className={cx(
          typography.mono,
          maxHeightClass,
          'p-4 pr-10 text-xs leading-relaxed text-fg-secondary',
          wrap
            ? 'overflow-y-auto whitespace-pre-wrap [overflow-wrap:anywhere]'
            : 'overflow-auto',
        )}
      >
        <code>
          {tokens.map((tok, i) => (
            // Tokens are a flat, order-stable slice of one static JSON string
            // for this render — index is a safe key here.
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} className={tok.className}>
              {tok.text}
            </span>
          ))}
        </code>
      </pre>
    </div>
  );
}
